import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseChartDirective } from 'ng2-charts';
import { BarController, BarElement, CategoryScale, Chart, Legend, LinearScale, Tooltip } from 'chart.js';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { RepositoryDetailResponse } from '../../core/models/repository.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';
import { MetricCardComponent } from '../../shared/components/metric-card/metric-card.component';
import { StatusChipComponent } from '../../shared/components/status-chip/status-chip.component';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

@Component({
  selector: 'app-repository-detail-page',
  standalone: true,
  imports: [DecimalPipe, BaseChartDirective, LocalDatePipe, MetricCardComponent, StatusChipComponent],
  templateUrl: './repository-detail-page.component.html',
  styleUrl: './repository-detail-page.component.scss',
})
export class RepositoryDetailPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<RepositoryDetailResponse | null>(null);
  readonly hasCategoryData = signal(false);
  readonly hasRecentPullRequests = signal(false);
  readonly categoryChartData = signal({
    labels: [] as string[],
    datasets: [
      {
        label: 'Issues',
        data: [] as number[],
        backgroundColor: ['#1d4ed8', '#f59e0b', '#16a34a', '#ef4444', '#8b5cf6'],
        borderRadius: 10,
      },
    ],
  });

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  private load(): void {
    const repositoryId = this.route.snapshot.paramMap.get('id');

    if (!repositoryId) {
      this.error.set('Repository id is missing.');
      this.loading.set(false);
      return;
    }

    this.analyticsService
      .getRepository(repositoryId, this.dateRange.toQueryParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const activeCategories = response.categoryBreakdown.filter((item) => item.count > 0);
          const recentPullRequests = response.recentPullRequests.filter(
            (item) => item.totalIssues > 0 || item.verdict !== 'Approved',
          );

          this.data.set(response);
          this.hasCategoryData.set(activeCategories.length > 0);
          this.hasRecentPullRequests.set(recentPullRequests.length > 0);
          this.categoryChartData.set({
            labels: activeCategories.map((item) => item.category),
            datasets: [
              {
                ...this.categoryChartData().datasets[0],
                data: activeCategories.map((item) => item.count),
              },
            ],
          });
          this.data.set({
            ...response,
            categoryBreakdown: activeCategories,
            recentPullRequests,
          });
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Repository details could not be loaded.');
          this.loading.set(false);
        },
      });
  }
}
